// src/core/pipeline.ts (full version, replacing the skeleton)
import type { DitherParams, Palette } from './types';
import {
  createFboTex, type FboTex, uploadImage, readPixels,
} from './gl';
import { createPreprocessProgram, runPreprocess, type PreprocessProgram } from './preprocess';
import { createOrderedProgram, uploadPalette, type OrderedProgram } from '../algorithms/ordered/_runner';
import { uploadBayerTexture } from '../algorithms/ordered/bayer';
import { uploadBlueNoiseTexture } from '../algorithms/ordered/blue-noise';
import { uploadWhiteNoiseTexture } from '../algorithms/ordered/white-noise';
import { createHalftoneProgram, SHAPE_INDEX, type HalftoneProgram } from '../algorithms/halftone/_runner';
import { HALFTONE_SHAPE_FROM_ID } from '../algorithms/halftone';
import { ERROR_DIFFUSION_ALGORITHMS } from '../algorithms/error-diffusion';

export type CacheLevel = 'preprocess' | 'downscale' | 'dither';

const PREPROCESS_KEYS = ['contrast', 'midtones', 'highlights', 'blur', 'invert', 'threshold', 'thresholdEnabled'] as const;

export function computeDirtyLevels(prev: DitherParams, next: DitherParams): CacheLevel[] {
  const dirty = new Set<CacheLevel>();
  for (const k of PREPROCESS_KEYS) if (prev[k] !== next[k]) dirty.add('preprocess');
  if (
    prev.downscale.enabled !== next.downscale.enabled ||
    prev.downscale.w !== next.downscale.w ||
    prev.downscale.h !== next.downscale.h
  ) dirty.add('downscale');
  if (
    prev.styleId !== next.styleId || prev.scale !== next.scale ||
    prev.tile !== next.tile || prev.paletteId !== next.paletteId
  ) dirty.add('dither');
  if (dirty.has('preprocess')) return ['preprocess', 'downscale', 'dither'];
  if (dirty.has('downscale')) return ['downscale', 'dither'];
  if (dirty.has('dither')) return ['dither'];
  return [];
}

const ED_IDS = new Set(ERROR_DIFFUSION_ALGORITHMS.map(a => a.id));

export class Pipeline {
  private params: DitherParams;
  private srcTex: WebGLTexture | null = null;
  private srcSize: { w: number; h: number } = { w: 0, h: 0 };

  private fboPre: FboTex | null = null;
  private fboDown: (FboTex & { w: number; h: number }) | null = null;
  private fboScaled: (FboTex & { w: number; h: number }) | null = null;
  private fboDitherSmall: (FboTex & { w: number; h: number }) | null = null;
  private fboDither: (FboTex & { w: number; h: number }) | null = null;

  private prePass: PreprocessProgram;
  private orderedPass: OrderedProgram;
  private halftonePass: HalftoneProgram;

  // GL resource caches (key → texture); rebuilt only when inputs change.
  private palCache = new Map<string, { tex: WebGLTexture; size: number }>();
  private bayerCache = new Map<number, { tex: WebGLTexture; size: number }>();
  private blueNoiseTex: { tex: WebGLTexture; size: number } | null = null;
  private whiteNoiseTex: { tex: WebGLTexture; size: number } | null = null;

  private worker: Worker;
  private workerCounter = 0;
  private workerPending = new Map<
    number,
    { resolve: (out: Uint8ClampedArray) => void; reject: (err: Error) => void }
  >();

  private dirty: Set<CacheLevel> = new Set(['preprocess', 'downscale', 'dither']);

  constructor(
    private gl: WebGL2RenderingContext,
    initial: DitherParams,
    private getPalette: (id: string) => Palette,
  ) {
    this.params = initial;
    this.prePass = createPreprocessProgram(gl);
    this.orderedPass = createOrderedProgram(gl);
    this.halftonePass = createHalftoneProgram(gl);
    this.worker = new Worker(
      new URL('../workers/error-diffusion.worker.ts', import.meta.url),
      { type: 'module' },
    );
    this.worker.addEventListener(
      'message',
      (e: MessageEvent<{ id: number; buffer?: Uint8ClampedArray; error?: string }>) => {
        const cb = this.workerPending.get(e.data.id);
        if (!cb) return;
        this.workerPending.delete(e.data.id);
        if (e.data.error) cb.reject(new Error(e.data.error));
        else cb.resolve(e.data.buffer!);
      },
    );
  }

  async setSource(src: ImageBitmap): Promise<void> {
    const gl = this.gl;
    if (this.srcTex) gl.deleteTexture(this.srcTex);
    if (this.fboPre) {
      gl.deleteFramebuffer(this.fboPre.fbo);
      gl.deleteTexture(this.fboPre.tex);
    }
    this.srcSize = { w: src.width, h: src.height };
    this.srcTex = uploadImage(gl, src);
    this.fboPre = createFboTex(gl, src.width, src.height);
    this.dirty = new Set(['preprocess', 'downscale', 'dither']);
  }

  setParams(next: DitherParams): void {
    for (const lvl of computeDirtyLevels(this.params, next)) this.dirty.add(lvl);
    this.params = next;
  }

  private outSize(): { w: number; h: number } {
    if (this.params.downscale.enabled) {
      return { w: this.params.downscale.w, h: this.params.downscale.h };
    }
    return this.srcSize;
  }

  async render(): Promise<ImageData> {
    if (!this.srcTex || !this.fboPre) throw new Error('no source loaded');
    const gl = this.gl;
    const { w: srcW, h: srcH } = this.srcSize;
    const { w: outW, h: outH } = this.outSize();

    if (this.dirty.has('preprocess')) {
      runPreprocess(gl, this.prePass, this.srcTex, this.fboPre, srcW, srcH, this.params);
    }

    if (this.dirty.has('downscale')) {
      if (!this.fboDown || this.fboDown.w !== outW || this.fboDown.h !== outH) {
        if (this.fboDown) {
          gl.deleteFramebuffer(this.fboDown.fbo);
          gl.deleteTexture(this.fboDown.tex);
        }
        const fbt = createFboTex(gl, outW, outH);
        this.fboDown = { ...fbt, w: outW, h: outH };
      }
      this.blit(this.fboPre.tex, this.fboDown, srcW, srcH, outW, outH);
    }

    const inputFbo = this.params.downscale.enabled ? this.fboDown! : this.fboPre;

    // Universal pre-dither downscale: when scale > 1, dither at (outW/scale, outH/scale)
    // and NEAREST-upscale back to (outW, outH) for chunky-pixel effect on all algorithms.
    const scale = Math.max(1, Math.floor(this.params.scale));
    const sw = Math.max(1, Math.floor(outW / scale));
    const sh = Math.max(1, Math.floor(outH / scale));

    let ditherSrcFbo: FboTex & { w: number; h: number };
    let ditherSrcW: number;
    let ditherSrcH: number;
    if (scale > 1) {
      if (!this.fboScaled || this.fboScaled.w !== sw || this.fboScaled.h !== sh) {
        if (this.fboScaled) {
          gl.deleteFramebuffer(this.fboScaled.fbo);
          gl.deleteTexture(this.fboScaled.tex);
        }
        const fbt = createFboTex(gl, sw, sh);
        this.fboScaled = { ...fbt, w: sw, h: sh };
      }
      // NEAREST downscale (blit uses TEXTURE_2D NEAREST filter set in createFboTex)
      this.blit(inputFbo.tex, this.fboScaled, outW, outH, sw, sh);
      ditherSrcFbo = this.fboScaled;
      ditherSrcW = sw;
      ditherSrcH = sh;
    } else {
      ditherSrcFbo = inputFbo as FboTex & { w: number; h: number };
      ditherSrcW = outW;
      ditherSrcH = outH;
    }

    if (this.dirty.has('dither')) {
      // dither output size
      const dw = ditherSrcW;
      const dh = ditherSrcH;
      if (!this.fboDitherSmall || this.fboDitherSmall.w !== dw || this.fboDitherSmall.h !== dh) {
        if (this.fboDitherSmall) {
          gl.deleteFramebuffer(this.fboDitherSmall.fbo);
          gl.deleteTexture(this.fboDitherSmall.tex);
        }
        const fbt = createFboTex(gl, dw, dh);
        this.fboDitherSmall = { ...fbt, w: dw, h: dh };
      }
      if (!this.fboDither || this.fboDither.w !== outW || this.fboDither.h !== outH) {
        if (this.fboDither) {
          gl.deleteFramebuffer(this.fboDither.fbo);
          gl.deleteTexture(this.fboDither.tex);
        }
        const fbt = createFboTex(gl, outW, outH);
        this.fboDither = { ...fbt, w: outW, h: outH };
      }
      const palette = this.getPalette(this.params.paletteId);
      if (this.params.styleId === 'none' || ED_IDS.has(this.params.styleId)) {
        // CPU path: read at (dw, dh), dither, upload back
        const inputPixels = readPixels(gl, ditherSrcFbo.fbo, dw, dh);
        const result = await this.runWorker(this.params.styleId, dw, dh, palette, inputPixels);
        this.uploadToFbo(this.fboDitherSmall, dw, dh, result);
      } else if (this.params.styleId.startsWith('halftone-')) {
        const palTex = this.getPaletteTex(palette);
        this.runHalftone(ditherSrcFbo.tex, this.fboDitherSmall, dw, dh, palTex);
      } else {
        // ordered: bayer | blue-noise | white-noise
        const palTex = this.getPaletteTex(palette);
        let thr: { tex: WebGLTexture; size: number };
        if (this.params.styleId === 'bayer') {
          const sizes = [2, 4, 8, 16];
          const n = sizes[Math.min(this.params.tile, 4) - 1] ?? 4;
          thr = this.getBayerTex(n);
        } else if (this.params.styleId === 'blue-noise') {
          // uploadBlueNoiseTexture caches internally; we mirror the handle here
          // so dispose() can release it.
          thr = this.blueNoiseTex ?? (this.blueNoiseTex = await uploadBlueNoiseTexture(gl));
        } else {
          thr = this.getWhiteNoiseTex();
        }
        this.runOrdered(ditherSrcFbo.tex, this.fboDitherSmall, dw, dh, palTex, thr);
      }
      // NEAREST-upscale back to fboDither at (outW, outH). When scale==1, dw==outW
      // so this is essentially a 1:1 copy — kept uniform to keep code simple.
      this.blit(this.fboDitherSmall.tex, this.fboDither, dw, dh, outW, outH);
    }

    this.dirty.clear();
    return this.toImageData(this.fboDither!, outW, outH);
  }

  private blit(srcTex: WebGLTexture, dst: FboTex, _srcW: number, _srcH: number, dstW: number, dstH: number) {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, dstW, dstH);
    gl.useProgram(this.prePass.program); // reuse fullscreen pipeline w/ identity params
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    gl.uniform1i(this.prePass.uniforms.u_src, 0);
    gl.uniform1f(this.prePass.uniforms.u_contrast, 0);
    gl.uniform1f(this.prePass.uniforms.u_midtones, 1);
    gl.uniform1f(this.prePass.uniforms.u_highlights, 0);
    gl.uniform1f(this.prePass.uniforms.u_blur, 0);
    gl.uniform1f(this.prePass.uniforms.u_threshold, 0);
    gl.uniform1i(this.prePass.uniforms.u_invert, 0);
    gl.uniform2f(this.prePass.uniforms.u_texel, 1 / dstW, 1 / dstH);
    gl.bindVertexArray(this.prePass.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private runOrdered(
    srcTex: WebGLTexture, dst: FboTex, w: number, h: number,
    palTex: { tex: WebGLTexture; size: number },
    thr: { tex: WebGLTexture; size: number },
  ) {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.orderedPass.program);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, srcTex);
    gl.uniform1i(this.orderedPass.u.src, 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, thr.tex);
    gl.uniform1i(this.orderedPass.u.threshold, 1);
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, palTex.tex);
    gl.uniform1i(this.orderedPass.u.palette, 2);
    gl.uniform2f(this.orderedPass.u.srcSize, w, h);
    gl.uniform2f(this.orderedPass.u.thresholdSize, thr.size, thr.size);
    gl.uniform1f(this.orderedPass.u.paletteSize, palTex.size);
    // u_scale handled upstream by pre-dither downscale; pass 1 here.
    gl.uniform1f(this.orderedPass.u.scale, 1);
    gl.bindVertexArray(this.orderedPass.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private runHalftone(
    srcTex: WebGLTexture, dst: FboTex, w: number, h: number,
    palTex: { tex: WebGLTexture; size: number },
  ) {
    const gl = this.gl;
    const shape = HALFTONE_SHAPE_FROM_ID[this.params.styleId]!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.halftonePass.program);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, srcTex);
    gl.uniform1i(this.halftonePass.u.src, 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, palTex.tex);
    gl.uniform1i(this.halftonePass.u.palette, 1);
    gl.uniform2f(this.halftonePass.u.srcSize, w, h);
    gl.uniform1f(this.halftonePass.u.paletteSize, palTex.size);
    // u_scale handled upstream by pre-dither downscale; pass 1 here.
    gl.uniform1f(this.halftonePass.u.scale, 1);
    gl.uniform1f(this.halftonePass.u.angleRad, ((this.params.tile - 1) / 15) * (Math.PI / 2));
    gl.uniform1i(this.halftonePass.u.shape, SHAPE_INDEX[shape]);
    gl.bindVertexArray(this.halftonePass.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private async runWorker(
    algorithmId: string, w: number, h: number, palette: Palette, buffer: Uint8ClampedArray,
  ): Promise<Uint8ClampedArray> {
    const id = ++this.workerCounter;
    return new Promise((resolve, reject) => {
      this.workerPending.set(id, { resolve, reject });
      this.worker.postMessage(
        { id, algorithm: algorithmId, width: w, height: h, palette: palette.colors, buffer },
        [buffer.buffer],
      );
    });
  }

  private getPaletteTex(palette: Palette): { tex: WebGLTexture; size: number } {
    const cached = this.palCache.get(palette.id);
    if (cached && cached.size === palette.colors.length) return cached;
    if (cached) this.gl.deleteTexture(cached.tex);
    const fresh = uploadPalette(this.gl, palette);
    this.palCache.set(palette.id, fresh);
    return fresh;
  }

  private getBayerTex(n: number): { tex: WebGLTexture; size: number } {
    const cached = this.bayerCache.get(n);
    if (cached) return cached;
    const fresh = uploadBayerTexture(this.gl, n);
    this.bayerCache.set(n, fresh);
    return fresh;
  }

  private getWhiteNoiseTex(): { tex: WebGLTexture; size: number } {
    if (this.whiteNoiseTex) return this.whiteNoiseTex;
    this.whiteNoiseTex = uploadWhiteNoiseTexture(this.gl);
    return this.whiteNoiseTex;
  }

  private uploadToFbo(dst: FboTex, w: number, h: number, data: Uint8ClampedArray) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, dst.tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, data);
  }

  private toImageData(src: FboTex, w: number, h: number): ImageData {
    const px = readPixels(this.gl, src.fbo, w, h);
    const img = new ImageData(w, h);
    img.data.set(px);
    return img;
  }

  dispose(): void {
    this.worker.terminate();

    const gl = this.gl;
    if (this.srcTex) { gl.deleteTexture(this.srcTex); this.srcTex = null; }
    if (this.fboPre) {
      gl.deleteFramebuffer(this.fboPre.fbo);
      gl.deleteTexture(this.fboPre.tex);
      this.fboPre = null;
    }
    if (this.fboDown) {
      gl.deleteFramebuffer(this.fboDown.fbo);
      gl.deleteTexture(this.fboDown.tex);
      this.fboDown = null;
    }
    if (this.fboScaled) {
      gl.deleteFramebuffer(this.fboScaled.fbo);
      gl.deleteTexture(this.fboScaled.tex);
      this.fboScaled = null;
    }
    if (this.fboDitherSmall) {
      gl.deleteFramebuffer(this.fboDitherSmall.fbo);
      gl.deleteTexture(this.fboDitherSmall.tex);
      this.fboDitherSmall = null;
    }
    if (this.fboDither) {
      gl.deleteFramebuffer(this.fboDither.fbo);
      gl.deleteTexture(this.fboDither.tex);
      this.fboDither = null;
    }

    for (const v of this.palCache.values()) gl.deleteTexture(v.tex);
    this.palCache.clear();
    for (const v of this.bayerCache.values()) gl.deleteTexture(v.tex);
    this.bayerCache.clear();
    if (this.whiteNoiseTex) { gl.deleteTexture(this.whiteNoiseTex.tex); this.whiteNoiseTex = null; }
    if (this.blueNoiseTex) { gl.deleteTexture(this.blueNoiseTex.tex); this.blueNoiseTex = null; }

    gl.deleteProgram(this.prePass.program);
    gl.deleteProgram(this.orderedPass.program);
    gl.deleteProgram(this.halftonePass.program);
  }
}
